import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompetitionSafe } from '../contexts/CompetitionContext';

const HamburgerMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUser, signOut } = useAuth();
  // Use safe version that returns null when not in a competition context
  const competitionContext = useCompetitionSafe();
  const competition = competitionContext?.competition;
  const isOrganizer = competitionContext?.isOrganizer;

  const handleNavigate = (path) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const competitionId = competition?.id;

  // Navigation menu items (always available)
  const navMenuItems = [
    { label: 'Home', path: '/', icon: '🏡' },
    { label: 'My Leagues', path: '/leagues', icon: '🎯' },
  ];

  // Competition menu items (only when in a competition)
  const competitionMenuItems = competitionId ? [
    { label: 'Dashboard', path: `/competition/${competitionId}`, icon: '🏠' },
    { label: 'Game Log', path: `/competition/${competitionId}/game-log`, icon: '📋' },
  ] : [];

  // Add player profile if in competition
  if (competitionId && currentUser) {
    competitionMenuItems.push({ label: 'My Profile', path: `/competition/${competitionId}/player/${currentUser.uid}`, icon: '👤' });
  }

  // Admin menu items (only for organizers)
  const adminMenuItems = [];
  if (isOrganizer && competitionId) {
    adminMenuItems.push({ label: 'Admin Panel', path: `/competition/${competitionId}/admin`, icon: '⚙️' });
  }

  return (
    <>
      {/* Hamburger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 right-4 z-50 p-2 rounded-lg bg-white shadow-lg hover:bg-gray-50 transition-colors"
        aria-label="Menu"
      >
        <div className="w-6 h-5 flex flex-col justify-between">
          <span
            className={`block h-0.5 w-6 bg-gray-800 transition-all duration-300 ${
              isOpen ? 'rotate-45 translate-y-2' : ''
            }`}
          />
          <span
            className={`block h-0.5 w-6 bg-gray-800 transition-all duration-300 ${
              isOpen ? 'opacity-0' : ''
            }`}
          />
          <span
            className={`block h-0.5 w-6 bg-gray-800 transition-all duration-300 ${
              isOpen ? '-rotate-45 -translate-y-2' : ''
            }`}
          />
        </div>
      </button>

      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Menu Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-2xl z-40 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Menu</h2>
            {competition && (
              <p className="text-sm text-gray-600 mt-1">{competition.name}</p>
            )}
          </div>

          {/* Menu Items */}
          <nav className="flex-1 overflow-y-auto p-4">
            {/* Navigation Section */}
            <div className="space-y-2">
              <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Navigation
              </div>
              {navMenuItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => handleNavigate(item.path)}
                  className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                >
                  <span className="text-2xl">{item.icon}</span>
                  <span className="font-medium text-gray-900">{item.label}</span>
                </button>
              ))}
            </div>

            {/* Competition Section */}
            {competitionMenuItems.length > 0 && (
              <>
                <div className="my-4 border-t border-gray-200"></div>
                <div className="space-y-2">
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Competition
                  </div>
                  {competitionMenuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors text-left"
                    >
                      <span className="text-2xl">{item.icon}</span>
                      <span className="font-medium text-gray-900">{item.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* Admin Section */}
            {adminMenuItems.length > 0 && (
              <>
                <div className="my-4 border-t border-gray-200"></div>
                <div className="space-y-2">
                  <div className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Admin
                  </div>
                  {adminMenuItems.map((item) => (
                    <button
                      key={item.path}
                      onClick={() => handleNavigate(item.path)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg bg-yellow-50 hover:bg-yellow-100 transition-colors text-left border border-yellow-200"
                    >
                      <span className="text-2xl">{item.icon}</span>
                      <span className="font-medium text-gray-900">{item.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200">
            {currentUser && (
              <div className="mb-4">
                <div className="text-xs text-gray-500 mb-1">Signed in as</div>
                <div className="font-medium text-gray-900 truncate">
                  {currentUser.displayName || currentUser.email}
                </div>
              </div>
            )}
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors"
            >
              <span>🚪</span>
              <span className="font-medium">Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default HamburgerMenu;
